import React, { useState, useRef, useEffect, useCallback } from 'react';
import { generateVoicePrompt, validateChapterData } from '../utils/voicePromptGenerator';
import { voiceApiClient, ERROR_TYPES } from '../utils/voiceApiClient';
import { loadApiKey, hasValidApiKey } from '../utils/apiKeyStorage';
import ApiKeySettings from './ApiKeySettings';

// Voice interaction states
const STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  LISTENING: 'listening',
  SPEAKING: 'speaking',
  PAUSED: 'paused',
  ERROR: 'error',
  ENDED: 'ended'
};

const VoiceQuizButton = ({ chapter }) => {
  const [connectionState, setConnectionState] = useState(STATES.DISCONNECTED);
  const [error, setError] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [conversationLog, setConversationLog] = useState([]);
  const [finalAssessment, setFinalAssessment] = useState(null);
  const [securityWarnings, setSecurityWarnings] = useState([]);
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  
  // Refs for WebRTC
  const mediaStreamRef = useRef(null);
  const nudgeTimerRef = useRef(null); // auto-continue timer
  const isPausedRef = useRef(false); // pause state for session management

  // Validate chapter data on mount
  const chapterValidation = validateChapterData(chapter);
  
  // Detect Safari iOS for better user experience
  const isSafariIOS = () => {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  };
  
  // Check for API key on mount
  useEffect(() => {
    setHasApiKey(hasValidApiKey());
  }, []);
  
  useEffect(() => {
    // Setup voice API client event handlers with stable references
    const onConnected = () => {
      setConnectionState(STATES.CONNECTED);
      setError(null);
    };
    const onSessionReady = () => {
      // Session is ready — trigger the first assistant turn without extra instructions.
      // Let the master system prompt (from generateVoicePrompt) drive the opening.
      setConnectionState(STATES.LISTENING);
      try {
        voiceApiClient.requestResponse(['audio', 'text']);
      } catch (e) {
        console.error('Failed to trigger initial greeting:', e);
      }
    };
    const onError = (errorData) => {
      setError(getErrorMessage(errorData.type, errorData.message));
      setConnectionState(STATES.ERROR);
    };
    const onDisconnected = () => {
      // When voice client disconnects, reset everything so user must restart
      setConnectionState(STATES.DISCONNECTED);
      resetAssessmentState();
    };
    const onTimeout = (message) => {
      setSecurityWarnings(prev => [...prev, message]);
      setConnectionState(STATES.ENDED);
    };
    const onLimit = (message) => {
      setSecurityWarnings(prev => [...prev, message]);
      setConnectionState(STATES.ENDED);
    };

    voiceApiClient.on('connected', onConnected);
    voiceApiClient.on('session-ready', onSessionReady);
    voiceApiClient.on('message', handleVoiceApiMessage);
    voiceApiClient.on('error', onError);
    voiceApiClient.on('disconnected', onDisconnected);
    voiceApiClient.on('session-timeout', onTimeout);
    voiceApiClient.on('session-limit-exceeded', onLimit);

    // Cleanup on unmount
    return () => {
      voiceApiClient.off('connected', onConnected);
      voiceApiClient.off('session-ready', onSessionReady);
      voiceApiClient.off('message', handleVoiceApiMessage);
      voiceApiClient.off('error', onError);
      voiceApiClient.off('disconnected', onDisconnected);
      voiceApiClient.off('session-timeout', onTimeout);
      voiceApiClient.off('session-limit-exceeded', onLimit);
      cleanup();
    };
  }, []);

  const resetAssessmentState = useCallback(() => {
    // Clear all assessment-specific state
    setConversationLog([]);
    setFinalAssessment(null);
    setError(null);
    setSecurityWarnings([]);
    
    // Clear any pending nudge timer
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
    
    // Reset pause state
    isPausedRef.current = false;
  }, []);

  const cleanup = useCallback(() => {
    voiceApiClient.disconnect();

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    setConnectionState(STATES.DISCONNECTED);
    setIsAudioEnabled(false);

    // Reset all assessment state
    resetAssessmentState();
  }, [resetAssessmentState]);

  const getErrorMessage = (errorType, message) => {
    switch (errorType) {
      case ERROR_TYPES.AUTHENTICATION:
        return `Authentication error: ${message}. Please check your OpenAI API key configuration.`;
      case ERROR_TYPES.API_LIMIT:
        return `API limit exceeded: ${message}. Please try again later or check your OpenAI account.`;
      case ERROR_TYPES.CONNECTION:
        return `Connection error: ${message}. Please check your internet connection.`;
      case ERROR_TYPES.AUDIO:
        return `Audio error: ${message}. Please check your microphone settings.`;
      case ERROR_TYPES.TIMEOUT:
        return `Connection timeout: ${message}. Please try again.`;
      default:
        return `Error: ${message}`;
    }
  };

  const handleVoiceApiMessage = (message) => {
    // If paused, ignore audio-related messages to prevent processing
    if (isPausedRef.current) {
      const audioRelatedTypes = [
        'conversation.item.input_audio_transcription',
        'response.audio_transcript',
        'response.audio'
      ];

      if (audioRelatedTypes.some(type => message.type.includes(type))) {
        return;
      }
    }

    switch (message.type) {
      case 'conversation.item.input_audio_transcription.completed':
        setConversationLog(prev => [...prev, {
          type: 'user',
          content: message.transcript,
          timestamp: new Date()
        }]);
        break;

      case 'response.audio_transcript.delta':
        setConnectionState(STATES.SPEAKING);
        // Cancel any pending nudge when assistant starts talking
        if (nudgeTimerRef.current) {
          clearTimeout(nudgeTimerRef.current);
          nudgeTimerRef.current = null;
        }
        break;

      case 'response.audio_transcript.done':
        setConversationLog(prev => [...prev, {
          type: 'assistant',
          content: message.transcript,
          timestamp: new Date()
        }]);
        setConnectionState(STATES.LISTENING);

        // Check if this looks like a final assessment
        if (message.transcript && message.transcript.includes('comprehension') && message.transcript.includes('%')) {
          setFinalAssessment(message.transcript);
          // Skip auto-continue when final assessment is given - conversation should end
          break;
        }

        // If assistant did not end with a clear question, gently auto-continue (only if not paused)
        if (!isPausedRef.current) {
          try {
            const endsWithQuestion = (t) => {
              if (!t) return false;
              const s = t.trim();
              // Simple heuristic: ends with question mark or a short prompt phrase
              return /[?？]$/.test(s) || /(your thoughts|what do you think|could you explain|can you|do you|how would you|why is|what is)$/i.test(s);
            };
            if (!endsWithQuestion(message.transcript)) {
              if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
              nudgeTimerRef.current = setTimeout(() => {
                // Only nudge if no response in progress and not paused
                if (!voiceApiClient.responseInProgress && !isPausedRef.current) {
                  voiceApiClient.requestResponse(['audio', 'text']);
                }
                nudgeTimerRef.current = null;
              }, 900); // brief pause to allow natural user start
            }
          } catch (e) {
            console.warn('Auto-continue nudge skipped:', e);
          }
        }
        break;

      default:
        // Handle other message types as needed
        break;
    }
  };

  const requestMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      mediaStreamRef.current = stream;
      setIsAudioEnabled(true);
      return stream;
    } catch (err) {
      console.error('Microphone access denied:', err);
      setError('Microphone access is required for voice interaction.');
      throw err;
    }
  };


  const connectToVoiceService = async (mediaStream) => {
    try {
      // Get the user's API key
      const userApiKey = loadApiKey();
      if (!userApiKey) {
        throw new Error('OpenAI API key not found. Please configure your API key in settings.');
      }

      const systemPrompt = generateVoicePrompt(chapter);
      await voiceApiClient.connect(systemPrompt, userApiKey, mediaStream);
    } catch (err) {
      console.error('Failed to connect to voice service:', err);
      setError(err.message || 'Failed to connect to voice service.');
      setConnectionState(STATES.ERROR);
      throw err;
    }
  };

  const interruptAssistant = () => {
    // Cancel the model's current response
    voiceApiClient.cancelResponse();
    setConnectionState(STATES.LISTENING);
  };



  const startVoiceAssessment = async () => {
    // Check if API key is configured first
    if (!hasValidApiKey()) {
      setShowApiKeySettings(true);
      return;
    }

    try {
      setError(null);
      setConnectionState(STATES.CONNECTING);
      setConversationLog([]);
      setFinalAssessment(null);
      // Request microphone access for WebRTC
      let stream;

      // Check for mediaDevices availability first
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        let errorMessage = 'Your browser does not support microphone access.';

        if (window.location.protocol !== 'https:') {
          errorMessage = 'Voice assessment requires a secure HTTPS connection.\n\nOn Safari iOS, microphone access is only available over HTTPS.\nPlease access this site using https:// instead of http://';
        } else if (isSafariIOS()) {
          errorMessage = 'Microphone access is not available in Safari iOS.\n\nThis could be due to:\n1. Safari iOS privacy settings\n2. Restricted browsing mode\n3. Enterprise/parental controls\n\nPlease check your Safari settings or try using Chrome on iOS.';
        }

        throw new Error(errorMessage);
      }

      try {
        stream = await requestMicrophoneAccess();
      } catch (err) {
        // Provide Safari iOS specific guidance
        let errorMessage = 'Microphone access is required for voice interaction.';
        if (isSafariIOS()) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            errorMessage = 'Microphone access denied. On Safari iOS, please:\n1. Tap the microphone icon in the address bar\n2. Select "Allow" for microphone access\n3. Refresh the page and try again';
          } else if (err.name === 'NotSupportedError') {
            errorMessage = 'Voice assessment requires a secure connection (HTTPS) on Safari iOS.';
          }
        }

        throw new Error(errorMessage);
      }

      // Connect to voice service with WebRTC
      await connectToVoiceService(stream);
      
    } catch (err) {
      setError(err.message || 'Failed to start voice assessment.');
      setConnectionState(STATES.ERROR);
      
      // Don't call full cleanup immediately - let user see the error
      // Just clean up the resources we might have partially created
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
      
      setIsAudioEnabled(false);
    }
  };

  const stopVoiceAssessment = () => {
    try {
      // Send final message to conclude the discussion
      if (voiceApiClient.isConnected) {
        voiceApiClient.sendClientEvent({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: 'Thank you, that concludes our discussion.' }]
          }
        });
      }
    } catch (err) {
      console.error('Error sending final message:', err);
    }
    
    setConnectionState(STATES.ENDED);
    setTimeout(cleanup, 2000); // Allow time for final responses
  };

  const pauseVoiceAssessment = () => {
    try {

      // Guard against multiple pause calls
      if (isPausedRef.current || connectionState === STATES.PAUSED) {
        return;
      }

      // Set paused ref FIRST to stop any audio processing
      isPausedRef.current = true;

      // Immediately pause audio playback and input
      if (voiceApiClient.isConnected) {
        voiceApiClient.pauseAudio();
      }

      // Then set React state for UI updates
      setConnectionState(STATES.PAUSED);

      // Clear any pending nudge timer
      if (nudgeTimerRef.current) {
        clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = null;
      }

      // Pause session timeout to prevent disconnection during pause
      if (voiceApiClient.isConnected) {
        voiceApiClient.pauseSessionTimeout();
      }

    } catch (err) {
      console.error('Error pausing voice assessment:', err);
    }
  };

  const resumeVoiceAssessment = () => {
    try {
      // Guard against multiple resume calls or resuming when not paused
      if (!isPausedRef.current || connectionState !== STATES.PAUSED) {
        return;
      }

      // Clear paused ref FIRST to allow audio processing to resume
      isPausedRef.current = false;

      // Resume audio playback and input
      if (voiceApiClient.isConnected) {
        voiceApiClient.resumeAudio();
        voiceApiClient.resumeSessionTimeout();
      }

      // Return to listening state
      setConnectionState(STATES.LISTENING);

      // Send context message to inform assistant about pause/resume instead of auto-continuing
      try {
        if (voiceApiClient.isConnected) {
          // Send a system context message to inform the assistant about the pause/resume
          voiceApiClient.sendClientEvent({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: 'user',
              content: [{
                type: 'input_text',
                text: '[PAUSE_RESUME_CONTEXT: The conversation was paused and has now resumed. Please check in with the student - ask if they need anything repeated or are ready to continue the discussion.]'
              }]
            }
          });

          // Now request a response so the assistant can check in with the user
          voiceApiClient.requestResponse(['audio', 'text']);
        }
      } catch (e) {
        console.warn('Failed to send resume context message:', e);
      }

    } catch (err) {
      console.error('Error resuming voice assessment:', err);
    }
  };

  const handleApiKeyUpdated = (hasKey) => {
    setHasApiKey(hasKey);
    setShowApiKeySettings(false);
    if (hasKey) {
      setError(null);
    }
  };

  const resetToDisconnected = () => {
    setConnectionState(STATES.DISCONNECTED);
    setError(null);
    resetAssessmentState();
  };

  const openApiKeySettings = () => {
    setShowApiKeySettings(true);
  };

  const getStatusDisplay = () => {
    switch (connectionState) {
      case STATES.CONNECTING:
        return { text: 'Connecting to Professor HAM...', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
      case STATES.CONNECTED:
        return { text: 'Connected - Starting conversation...', color: 'text-blue-600', bgColor: 'bg-blue-50' };
      case STATES.LISTENING:
        return { text: 'Professor HAM is listening...', color: 'text-green-600', bgColor: 'bg-green-50' };
      case STATES.SPEAKING:
        return { text: 'Professor HAM is speaking...', color: 'text-blue-600', bgColor: 'bg-blue-50' };
      case STATES.PAUSED:
        return { text: 'Discussion paused - Click resume to continue', color: 'text-orange-600', bgColor: 'bg-orange-50' };
      case STATES.ERROR:
        return { text: 'Connection error', color: 'text-red-600', bgColor: 'bg-red-50' };
      case STATES.ENDED:
        return { text: 'Assessment completed', color: 'text-gray-600', bgColor: 'bg-gray-50' };
      default:
        return null;
    }
  };

  const isActive = [STATES.CONNECTING, STATES.CONNECTED, STATES.LISTENING, STATES.SPEAKING].includes(connectionState);
  const isPaused = connectionState === STATES.PAUSED;
  const isError = connectionState === STATES.ERROR;
  const canStart = connectionState === STATES.DISCONNECTED && hasApiKey;
  const canStop = isActive || isPaused;
  const canPause = [STATES.LISTENING, STATES.SPEAKING].includes(connectionState);
  const canResume = isPaused;
  const canRetry = isError;
  const status = getStatusDisplay();
  const needsApiKey = !hasApiKey;

  // Don't render if chapter data is invalid
  if (!chapterValidation.isValid) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Voice Assessment Unavailable</h3>
        <p className="text-red-600 mb-2">Cannot load voice assessment for this chapter:</p>
        <ul className="list-disc list-inside text-red-600 text-sm">
          {chapterValidation.errors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold mb-1">Interactive Voice Assessment</h3>
          <p className="text-blue-100 text-sm">
            Have a one-on-one discussion with Professor HAM
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-blue-100">Chapter: {chapter.id}</div>
          <div className="text-sm font-medium">{chapter.title}</div>
        </div>
      </div>
      
      <div className="mb-4">
        <p className="text-sm text-blue-100 mb-2">
          📞 Voice conversation • 🎯 Personalized assessment • 🇿🇦 South African context
        </p>
        
        {status && (
          <div className={`${status.bgColor} ${status.color} px-3 py-2 rounded-md text-sm font-medium mb-3 flex items-center space-x-2`}>
            {connectionState === STATES.LISTENING && (
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            )}
            {connectionState === STATES.SPEAKING && (
              <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
            )}
            {connectionState === STATES.PAUSED && (
              <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
            )}
            <span>{status.text}</span>
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-md text-sm mb-3">
            <strong>Error:</strong> <span className="whitespace-pre-line">{error}</span>
            {isSafariIOS() && (
              <div className="mt-2 text-xs text-red-600">
                <strong>Debug tip:</strong> Open Safari's developer console (Settings → Advanced → Web Inspector) to see detailed error logs.
              </div>
            )}
          </div>
        )}

        {needsApiKey && (
          <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-3 py-2 rounded-md text-sm mb-3">
            <strong>⚠️ API Key Required:</strong> You need to configure your OpenAI API key to use voice assessment.
            <button 
              onClick={openApiKeySettings}
              className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
            >
              Configure API Key
            </button>
          </div>
        )}

        {!needsApiKey && isSafariIOS() && connectionState === STATES.DISCONNECTED && (
          <div className="bg-blue-100 border border-blue-300 text-blue-800 px-3 py-2 rounded-md text-sm mb-3">
            <strong>📱 Safari iOS Detected:</strong> When you tap "Start Voice Assessment", Safari will ask for microphone permission. Please allow access for the voice feature to work properly.
          </div>
        )}

        {securityWarnings.length > 0 && (
          <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-3 py-2 rounded-md text-sm mb-3">
            <strong>Session Info:</strong>
            <ul className="mt-1 text-xs">
              {securityWarnings.map((warning, index) => (
                <li key={index}>• {warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex space-x-3 mb-4">
        <button
          onClick={needsApiKey ? openApiKeySettings : startVoiceAssessment}
          disabled={!needsApiKey && !canStart}
          className="flex-1 bg-white text-blue-600 px-4 py-3 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {needsApiKey ? '🔑 Configure API Key' : canStart ? '🎤 Start Voice Assessment' : 'Assessment Active'}
        </button>
        
        {canPause && (
          <button
            onClick={pauseVoiceAssessment}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors"
          >
            ⏸️ Pause
          </button>
        )}
        
        {canResume && (
          <button
            onClick={resumeVoiceAssessment}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors"
          >
            ▶️ Resume
          </button>
        )}
        
        {canRetry && (
          <button
            onClick={resetToDisconnected}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors"
          >
            🔄 Try Again
          </button>
        )}

        {canStop && (
          <button
            onClick={stopVoiceAssessment}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors"
          >
            End Session
          </button>
        )}
      </div>

      {finalAssessment && (
        <div className="bg-white bg-opacity-20 rounded-lg p-4 mt-4">
          <h4 className="font-semibold text-white mb-2">📊 Final Assessment</h4>
          <p className="text-blue-100 text-sm whitespace-pre-wrap">{finalAssessment}</p>
        </div>
      )}

      <div className="text-xs text-blue-200 mt-4">
        <p className="mb-1">
          <strong>Before starting:</strong> Ensure you have a quiet environment and working microphone.
        </p>
        <p>
          <strong>Tip:</strong> Speak clearly and naturally - this is a discussion, not an exam!
        </p>
        {hasApiKey && (
          <p className="mt-2 text-blue-300">
            <button 
              onClick={openApiKeySettings}
              className="text-blue-200 hover:text-white underline text-xs"
            >
              ⚙️ Manage API Key
            </button>
          </p>
        )}
      </div>

      {/* API Key Settings Modal */}
      <ApiKeySettings
        isOpen={showApiKeySettings}
        onClose={() => setShowApiKeySettings(false)}
        onApiKeyUpdated={handleApiKeyUpdated}
      />
    </div>
  );
};

export default VoiceQuizButton;
